import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Mail, CheckCircle2, XCircle, RefreshCw, Loader2, ChevronLeft, ChevronRight, Search, Send } from "lucide-react";
import { toast } from "sonner";

const EVENT_LABELS = {
    generic: "Generica",
    credentials: "Credenziali",
    document_delivered: "Consegna documenti",
    document_assigned: "Assegnazione documenti",
    ack_requested: "Presa visione",
    employee_upload: "Doc. da dipendente",
    expiring_summary: "Riepilogo scadenze",
};

const PAGE_SIZE = 25;

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch (e) {
        return iso;
    }
};

export default function EmailLogPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState({ sent: 0, failed: 0 });
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("all");
    const [event, setEvent] = useState("all");
    const [page, setPage] = useState(1);
    const [resendRow, setResendRow] = useState(null);
    const [resendTo, setResendTo] = useState("");
    const [resending, setResending] = useState(false);

    const load = useCallback(async (opts = {}) => {
        setLoading(true);
        try {
            const { data } = await api.get("/email-logs", {
                params: {
                    q: opts.q ?? q,
                    status: opts.status ?? status,
                    event: opts.event ?? event,
                    page: opts.page ?? page,
                    page_size: PAGE_SIZE,
                },
            });
            setItems(data.items);
            setTotal(data.total);
            setStats(data.stats);
            setEmailEnabled(data.email_enabled !== false);
        } catch (e) {
            toast.error("Errore caricamento registro email");
        } finally {
            setLoading(false);
        }
    }, [q, status, event, page]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, event, page]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        load({ page: 1 });
    };

    const openResend = (row) => {
        setResendRow(row);
        setResendTo(row.to || "");
    };

    const handleResend = async () => {
        if (!resendRow) return;
        setResending(true);
        try {
            const { data } = await api.post(`/email-logs/${resendRow.id}/resend`, { to: resendTo.trim() });
            if (data.sent) {
                toast.success(`Email reinviata a ${data.to}`);
            } else {
                toast.error(data.error || "Reinvio non riuscito");
            }
            setResendRow(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore durante il reinvio");
        } finally {
            setResending(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="email-log-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notifiche</div>
                    <h1 className="mt-1 font-display text-2xl font-bold text-[#15498F]">Registro Email</h1>
                    <p className="mt-1 text-sm text-slate-500">
                        Tutte le email inviate dal portale: credenziali, documenti, scadenze e notifiche.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => load()}
                    disabled={loading}
                    data-testid="email-log-refresh"
                >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                    Aggiorna
                </Button>
            </div>

            {!emailEnabled && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="email-disabled-banner">
                    <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                        <b>Invio email disattivato.</b> Nessuna email parte dal portale (notifiche documenti, credenziali, scadenze).
                        Verranno riattivate prima della pubblicazione.
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <Mail className="h-4 w-4 text-[#376DB3]" /> Totale email
                    </div>
                    <div className="mt-1 font-mono-numbers text-2xl font-bold text-slate-800" data-testid="email-log-stat-total">
                        {stats.sent + stats.failed}
                    </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Inviate
                    </div>
                    <div className="mt-1 font-mono-numbers text-2xl font-bold text-emerald-600" data-testid="email-log-stat-sent">
                        {stats.sent}
                    </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <XCircle className="h-4 w-4 text-red-500" /> Non inviate
                    </div>
                    <div className="mt-1 font-mono-numbers text-2xl font-bold text-red-500" data-testid="email-log-stat-failed">
                        {stats.failed}
                    </div>
                </div>
            </div>

            {/* Filters */}
            <form onSubmit={handleSearch} className="mt-6 flex flex-wrap items-center gap-3">
                <div className="relative min-w-[220px] flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca destinatario o oggetto…"
                        className="pl-9"
                        data-testid="email-log-search-input"
                    />
                </div>
                <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                    <SelectTrigger className="w-[150px]" data-testid="email-log-status-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutti gli esiti</SelectItem>
                        <SelectItem value="sent">Inviate</SelectItem>
                        <SelectItem value="failed">Non inviate</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={event} onValueChange={(v) => { setEvent(v); setPage(1); }}>
                    <SelectTrigger className="w-[190px]" data-testid="email-log-event-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tutti i tipi</SelectItem>
                        {Object.entries(EVENT_LABELS).map(([k, label]) => (
                            <SelectItem key={k} value={k}>{label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button type="submit" variant="secondary" data-testid="email-log-search-btn">Cerca</Button>
            </form>

            {/* Table */}
            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-3">Data</th>
                                <th className="px-4 py-3">Destinatario</th>
                                <th className="px-4 py-3">Oggetto</th>
                                <th className="px-4 py-3">Tipo</th>
                                <th className="px-4 py-3">Esito</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="email-log-table-body">
                            {loading && items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                                        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-slate-400" data-testid="email-log-empty">
                                        Nessuna email trovata.
                                    </td>
                                </tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid="email-log-row">
                                        <td className="whitespace-nowrap px-4 py-3 font-mono-numbers text-xs text-slate-500">
                                            {fmtDate(row.at)}
                                        </td>
                                        <td className="px-4 py-3 font-medium text-[#376DB3]">{row.to}</td>
                                        <td className="max-w-[280px] truncate px-4 py-3 text-slate-700" title={row.subject}>
                                            {row.subject}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                                {EVENT_LABELS[row.event] || row.event}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.sent ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                    <CheckCircle2 className="h-3 w-3" /> Inviata
                                                </span>
                                            ) : (
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600"
                                                    title={row.error || ""}
                                                >
                                                    <XCircle className="h-3 w-3" /> Errore
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            {!row.sent && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-7 gap-1.5 text-xs"
                                                    disabled={!row.has_body}
                                                    title={row.has_body ? "Reinvia questa email" : "Contenuto non disponibile (email registrata prima dell'aggiornamento)"}
                                                    onClick={() => openResend(row)}
                                                    data-testid={`email-log-resend-${row.id}`}
                                                >
                                                    <Send className="h-3 w-3" /> Reinvia
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-500">
                    <span data-testid="email-log-total">{total} email — pagina {page} di {totalPages}</span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page <= 1 || loading}
                            onClick={() => setPage((p) => p - 1)}
                            data-testid="email-log-prev-page"
                        >
                            <ChevronLeft className="h-4 w-4" /> Precedente
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page >= totalPages || loading}
                            onClick={() => setPage((p) => p + 1)}
                            data-testid="email-log-next-page"
                        >
                            Successiva <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Resend dialog */}
            <Dialog open={!!resendRow} onOpenChange={(o) => !o && setResendRow(null)}>
                <DialogContent className="sm:max-w-md" data-testid="email-resend-dialog">
                    <DialogHeader>
                        <DialogTitle>Reinvia email</DialogTitle>
                        <DialogDescription>
                            {resendRow?.subject}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Destinatario</label>
                        <Input
                            value={resendTo}
                            onChange={(e) => setResendTo(e.target.value)}
                            placeholder="email@esempio.com"
                            data-testid="email-resend-to-input"
                        />
                        {resendRow?.error && (
                            <p className="text-xs text-red-500">Errore precedente: {resendRow.error}</p>
                        )}
                        <p className="text-xs text-slate-400">
                            Puoi correggere l'indirizzo prima di reinviare: il contenuto sarà identico all'originale.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setResendRow(null)} disabled={resending}>
                            Annulla
                        </Button>
                        <Button
                            onClick={handleResend}
                            disabled={resending || !resendTo.trim().includes("@")}
                            data-testid="email-resend-confirm-btn"
                        >
                            {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                            Reinvia
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

